/* ============================================================
   EMPRESS WISHLIST PAGE (Oct 2026)
   Draws her saved products from window.EEWish (assets/wishlist.js).
   Each card is the theme's own Liquid (sections/wishlist-card.liquid)
   rendered for that product through the Section Rendering API, so
   price, stock and sizes are always live.
   She picks or changes a size right on the card, then adds to bag.
   ============================================================ */
(function () {
'use strict';
var root = document.querySelector('[data-ee-wish-page]');
if (!root || root.__eeWishPage) return;
root.__eeWishPage = true;

var grid = root.querySelector('[data-ee-wish-grid]');
var empty = root.querySelector('[data-ee-wish-empty]');
var loading = root.querySelector('[data-ee-wish-loading]');
var SECTION = root.getAttribute('data-card-section') || 'wishlist-card';
var LIMIT = 6; /* fetches at once */
var cache = {}; /* handle -> Promise<Element|null> */

function t(key, fallback) { return window.EEWish && window.EEWish.text ? window.EEWish.text(key, fallback) : fallback; }

function cardFor(handle) {
  if (!cache[handle]) {
    cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-ee-wcard]');
      })
      .catch(function () { delete cache[handle]; return null; });
  }
  return cache[handle].then(function (el) { return el ? el.cloneNode(true) : null; });
}

/* Run fetches a few at a time, keep the list order. */
function inBatches(items, fn) {
  var out = new Array(items.length), i = 0;
  function next() {
    if (i >= items.length) return Promise.resolve();
    var k = i++;
    return fn(items[k]).then(function (r) { out[k] = r; return next(); });
  }
  var lanes = [];
  for (var n = 0; n < Math.min(LIMIT, items.length); n++) lanes.push(next());
  return Promise.all(lanes).then(function () { return out; });
}

function selectSize(card, variantId) {
  var chosen = null;
  card.querySelectorAll('[data-ee-wsize]').forEach(function (b) {
    var on = !!variantId && b.getAttribute('data-ee-wsize') === String(variantId);
    b.setAttribute('aria-pressed', String(on));
    if (on) chosen = b;
  });
  var atc = card.querySelector('[data-ee-watc]');
  if (!atc) return;
  /* Early access while the gate is on, or not dropped yet: the card's own label stands */
  if (card.hasAttribute('data-locked')) { atc.disabled = true; return; }
  var single = card.hasAttribute('data-single-variant');
  var available = single ? card.getAttribute('data-single-available') !== 'false' : !!chosen && !chosen.disabled;
  atc.disabled = !available;
  atc.textContent = single
    ? (available ? t('add', 'Add to bag') : t('sold', 'Sold out'))
    : (!chosen ? t('pick', 'Pick a size') : (chosen.disabled ? t('sold', 'Sold out') : t('add', 'Add to bag')));
  var low = card.querySelector('[data-ee-wlow]');
  if (low) {
    var left = chosen ? parseInt(chosen.getAttribute('data-left') || '', 10) : NaN;
    low.textContent = left > 0 && left <= 3 ? t('left', 'Only [n] left').replace('[n]', String(left)) : '';
  }
}

function render() {
  var items = window.EEWish ? window.EEWish.items().filter(function (it) { return it.h; }) : [];
  if (empty) empty.hidden = items.length > 0;
  if (!items.length) {
    grid.innerHTML = '';
    if (loading) loading.hidden = true;
    return Promise.resolve();
  }
  if (loading && !grid.children.length) loading.hidden = false;
  return inBatches(items, function (it) {
    return cardFor(it.h).then(function (card) {
      if (!card) return null;
      card.setAttribute('data-product-id', it.p);
      selectSize(card, it.v);
      return card;
    });
  }).then(function (cards) {
    var frag = document.createDocumentFragment();
    cards.forEach(function (c) { if (c) frag.appendChild(c); });
    grid.innerHTML = '';
    grid.appendChild(frag);
    if (loading) loading.hidden = true;
    if (empty) empty.hidden = grid.children.length > 0;
  });
}

/* Add the way the theme's own product form does (theme.js ProductForm): the drawer, count,
   dot and free-shipping bar all re-render from one cart:change, and the drawer opens.
   Pre-sale items carry Globo's note like every other add (assets/globo-presale-note.js). */
function addToBag(card, btn) {
  if (card.hasAttribute('data-locked')) return;
  var single = card.getAttribute('data-single-variant');
  var picked = card.querySelector('[data-ee-wsize][aria-pressed="true"]');
  var id = single || (picked && picked.getAttribute('data-ee-wsize'));
  if (!id) return;
  btn.disabled = true;
  var label = btn.textContent;
  var item = { id: Number(id), quantity: 1 };
  var props = window.EmpressPresaleNote ? window.EmpressPresaleNote.propsFor(id) : undefined;
  if (props) item.properties = props;
  var sections = [];
  try { document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })); } catch (e) {}
  var added;
  fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items: [item], sections: sections.join(','), sections_url: '/variants/' + id })
  }).then(function (r) {
    return r.json().then(function (j) {
      if (!r.ok) throw new Error(j && (j.description || j.message) || '');
      added = j;
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (c) { return c.json(); });
    });
  }).then(function (cart) {
    btn.textContent = t('added', 'Added');
    var settings = window.themeVariables && window.themeVariables.settings || {};
    if (settings.cartType === 'page') { window.location.href = '/cart'; return; }
    cart.sections = added && added.sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
    /* (GA4's add_to_cart comes from Shopify's own pixel for every cart add: don't send a second one) */
    try { if (window.clarity) window.clarity('event', 'wishlist_add_to_cart'); } catch (e) {}
    setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 2200);
  }).catch(function (err) {
    btn.disabled = false;
    btn.textContent = label;
    if (window.EEWish && window.EEWish.toast) window.EEWish.toast(err && err.message ? String(err.message) : t('sold', 'Sold out'), false);
  });
}

root.addEventListener('click', function (e) {
  var el = e.target;
  if (!el || !el.closest) return;
  var card = el.closest('[data-ee-wcard]');
  if (!card) return;
  var p = card.getAttribute('data-product-id');
  var size = el.closest('[data-ee-wsize]');
  if (size) {
    e.preventDefault();
    selectSize(card, size.getAttribute('data-ee-wsize'));
    if (window.EEWish && !size.disabled) window.EEWish.setSize(p, size.getAttribute('data-ee-wsize'));
    return;
  }
  var atc = el.closest('[data-ee-watc]');
  if (atc) { e.preventDefault(); addToBag(card, atc); return; }
  var del = el.closest('[data-ee-wremove]');
  if (del) {
    e.preventDefault();
    card.remove();
    if (window.EEWish) window.EEWish.remove(p);
    if (empty) empty.hidden = grid.children.length > 0;
  }
});

/* Redraw when the list changes elsewhere (another tab, account sync, Back button), but not for our own size taps. */
var last = '';
function key() {
  return window.EEWish ? window.EEWish.items().map(function (it) { return it.p; }).join(',') : '';
}
document.addEventListener('ee:wishlist', function () {
  var k = key();
  if (k === last) return;
  last = k;
  render();
});

function boot() {
  last = key();
  var w = window.EEWish;
  /* Signed in on a new device: her list is still on its way from her account */
  if (w && w.synced === false && !w.items().length && w.ready) {
    if (loading) loading.hidden = false;
    w.ready.then(function () { last = key(); render(); });
    return;
  }
  render();
}
if (window.EEWish) boot(); else document.addEventListener('DOMContentLoaded', boot);
})();
